/**
 * UserWithdrawRequestPage Component
 * 탈퇴 요청 관리 페이지
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useUserWithdrawData } from '../hooks/useUserWithdrawData';
import { useDelayedLoading } from '@/shared/hooks/useDelayedLoading';
import { usePagination } from '@/shared/hooks/usePagination';
import Loading from '@/shared/components/feedback/Loading';
import { ApproveWithdrawalRequestDto } from '../api/userWithdrawApi';
import { FilterState, INITIAL_FILTERS } from "@/features/system/userWithdraw/utils/userWithdrawHelpers";
import FilterSection from './FilterSection';
import UserWithdrawTable from "./UserWithdrawTable";
import '@/shared/styles/search-form.css';
import '@/shared/styles/common-table.css';
import '../styles/UserWithdrawRequestPage.css';

const ITEMS_PER_PAGE = 15;

// ======== 컴포넌트 ========

const UserWithdrawRequestPage: React.FC = () => {
    // ======== 훅 ========

    const {
        withdrawRequests,
        loading,
        contStatusCodes,
        withdrawalStatusCodes,
        fetchWithdrawRequests,
        approveWithdrawal,
    } = useUserWithdrawData();

    const showLoading = useDelayedLoading(loading);

    const { currentPage, totalPages, currentItems, goToPage, resetPagination } = usePagination({
        items: withdrawRequests,
        itemsPerPage: ITEMS_PER_PAGE,
    });

    // ======== 상태 ========

    const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

    // ======== 핸들러 ========

    const handleSearch = useCallback(() => {
        resetPagination();
        setSelectedItems(new Set());
        const params: {
            riderName?: string;
            suppName?: string;
            contStatus?: string;
            withdrawalStatusCd?: string;
        } = {};
        if (filters.riderName) params.riderName = filters.riderName;
        if (filters.suppName) params.suppName = filters.suppName;
        if (filters.contStatus) params.contStatus = filters.contStatus;
        if (filters.withdrawalStatusCd) params.withdrawalStatusCd = filters.withdrawalStatusCd;
        fetchWithdrawRequests(Object.keys(params).length > 0 ? params : undefined);
    }, [filters, fetchWithdrawRequests, resetPagination]);

    const handleSelectAll = useCallback(() => {
        const selectableItems = currentItems.filter(item => {
            const isApproved = item.withdrawalStatusCd === 'APPROVED' || item.withdrawalStatusCdName === '승인';
            return !isApproved;
        });
        if (selectableItems.length > 0 && selectedItems.size === selectableItems.length) {
            setSelectedItems(new Set());
        } else {
            setSelectedItems(new Set(selectableItems.map(item => `${item.riderId}-${item.suppId}`)));
        }
    }, [currentItems, selectedItems]);

    const handleSelectItem = useCallback((riderId: string, suppId: string) => {
        const key = `${riderId}-${suppId}`;
        const newSelected = new Set(selectedItems);
        if (newSelected.has(key)) {
            newSelected.delete(key);
        } else {
            newSelected.add(key);
        }
        setSelectedItems(newSelected);
    }, [selectedItems]);

    const handleApprove = useCallback(async () => {
        if (selectedItems.size === 0) return;
        const requests: ApproveWithdrawalRequestDto[] = Array.from(selectedItems).map(key => {
            const [riderId, suppId] = key.split('-');
            return { riderId, suppId };
        });
        const result = await approveWithdrawal(requests);
        if (result) setSelectedItems(new Set());
    }, [selectedItems, approveWithdrawal]);

    // ======== 파생 상태 ========

    const isAllSelected = useMemo(() => {
        if (currentItems.length === 0) return false;
        const selectableItems = currentItems.filter(item => {
            const isApproved = item.withdrawalStatusCd === 'APPROVED' || item.withdrawalStatusCdName === '승인';
            return !isApproved;
        });
        if (selectableItems.length === 0) return false;
        return selectableItems.every(item => selectedItems.has(`${item.riderId}-${item.suppId}`));
    }, [currentItems, selectedItems]);

    // ======== 렌더링 ========

    if (showLoading) return <Loading />;

    return (
        <div className="user-withdraw-request-page">
            <div className="page-header">
                <h1 className="page-title">탈퇴 요청 관리</h1>
            </div>

            <FilterSection
                filters={filters}
                contStatusCodes={contStatusCodes}
                withdrawalStatusCodes={withdrawalStatusCodes}
                onFilterChange={setFilters}
                onSearch={handleSearch}
            />

            <UserWithdrawTable
                withdrawRequests={withdrawRequests}
                currentItems={currentItems}
                currentPage={currentPage}
                totalPages={totalPages}
                selectedItems={selectedItems}
                isAllSelected={isAllSelected}
                onSelectAll={handleSelectAll}
                onSelectItem={handleSelectItem}
                onApprove={handleApprove}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default UserWithdrawRequestPage;
